import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'chopper.precise.v1';
const FRESH_MS = 2 * 60 * 1000; // 2 minutes — fresh enough for "what's overhead now?"

function readCached() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCached(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/**
 * @typedef {Object} MyLocationState
 * @property {{latitude:number, longitude:number, accuracy:number, ts:number}|null} location
 * @property {'idle'|'locating'|'granted'|'denied'|'unavailable'} status
 * @property {() => void} refresh
 */

/**
 * Resolve the visitor's precise lat/lng via the browser geolocation API. Caches
 * the most recent fix in localStorage with a 2-minute freshness window so the
 * "near me" panel renders instantly on revisit while still re-requesting a
 * fresh fix in the background.
 *
 * Decoupled from useScope() (which keeps a coarser geohash3 personalization
 * scope). They both call getCurrentPosition but the browser dedupes the
 * permission prompt.
 *
 * @returns {MyLocationState}
 */
export function useMyLocation() {
  const cached = typeof window !== 'undefined' ? readCached() : null;
  const cachedFresh = cached && cached.location && Date.now() - cached.location.ts < FRESH_MS;

  const [location, setLocation] = useState(() => (cached && cached.location) || null);
  const [status, setStatus] = useState(() => {
    if (cachedFresh) return 'granted';
    if (cached && cached.denied) return 'denied';
    if (typeof navigator === 'undefined' || !navigator.geolocation) return 'unavailable';
    return 'idle';
  });

  const request = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unavailable');
      return;
    }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy ?? 0,
          ts: Date.now(),
        };
        setLocation(next);
        setStatus('granted');
        writeCached({ location: next });
      },
      (err) => {
        if (err && err.code === 1) {
          setStatus('denied');
          writeCached({ denied: true, ts: Date.now() });
        } else {
          setStatus('unavailable');
        }
      },
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: 10_000 },
    );
  }, []);

  useEffect(() => {
    // Auto-request on mount unless we already have a fresh fix or the user
    // has previously denied permission. The denial is sticky until they
    // explicitly tap the refresh affordance.
    if (cachedFresh) return;
    if (cached && cached.denied) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { location, status, refresh: request };
}
